import { useCallback, useEffect, useMemo } from 'react';

import inRange from 'lodash/inRange';

import { DrawerProps, notify } from '@cast/design-system';
import { parseApiError } from '@cast/api-client';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { useCreateRestrictionSilenceMutation } from 'hooks/mutations/restriction-silences/useCreateRestrictionSilenceMutation';

import { WorkloadDrawerView } from './_components/WorkloadDrawerView';
import { useRestrictionDetailsData } from './_hooks/useRestrictionDetailsData';
import { useRestrictionDetailsQuery } from './_hooks/useRestrictionDetailsQuery';
import { useRestrictionsDrawerState } from './_hooks/useRestrictionsDrawerState';
import { useWorkloadData } from './_hooks/useWorkloadData';
import { buildLineRange, isItemClickable } from './_utils/selectionUtils';
import type { RestrictionItem, RestrictionsDrawerProps } from './types';

type Props = DrawerProps & RestrictionsDrawerProps;

export const RestrictionsDrawer = (props: Props) => {
  const {
    restrictions,
    resourceName,
    resourceNamespace,
    resourceKind,
    ref,
    ...drawerProps
  } = props;

  const {
    activeManifestId,
    highlightedLines,
    selectedItemId,
    handleItemClick,
  } = useRestrictionsDrawerState();

  const { cluster } = useCluster();

  const { mutate: createRestrictionSilence } =
    useCreateRestrictionSilenceMutation();

  const handleSuppressWorkload = useCallback(() => {
    createRestrictionSilence(
      {
        organizationId: cluster.organizationId!,
        clusterId: cluster.id,
        restrictionSilence: {
          workloadName: resourceName,
          namespace: resourceNamespace || '',
          workloadKind: resourceKind || '',
        },
      },
      {
        onError: (error) => notify.error(parseApiError(error)),
      }
    );
  }, [
    cluster.id,
    cluster.organizationId,
    createRestrictionSilence,
    resourceName,
    resourceNamespace,
    resourceKind,
  ]);

  const handleSuppressRestrictionType = useCallback(
    (restrictionType: string) => {
      createRestrictionSilence(
        {
          organizationId: cluster.organizationId!,
          clusterId: cluster.id,
          restrictionSilence: {
            workloadName: resourceName,
            namespace: resourceNamespace || '',
            workloadKind: resourceKind || '',
            restrictionType,
          },
        },
        {
          onError: (error) => notify.error(parseApiError(error)),
        }
      );
    },
    [
      cluster.id,
      cluster.organizationId,
      createRestrictionSilence,
      resourceName,
      resourceNamespace,
      resourceKind,
    ]
  );

  const workloadData = useWorkloadData({
    restrictions,
    resourceName,
    resourceNamespace,
    resourceKind,
  });

  const { data: restrictionDetailsData, isLoading: isLoadingDetails } =
    useRestrictionDetailsQuery({
      workloadData,
    });

  const { categories, yamlFiles } = useRestrictionDetailsData(
    restrictionDetailsData
  );

  const flatItems = useMemo(
    () => categories.flatMap((category) => category.items),
    [categories]
  );

  const selectItem = useCallback(
    (item: RestrictionItem) => {
      if (!isItemClickable(item)) return;

      handleItemClick(
        item.itemId ?? '',
        item.manifestId,
        buildLineRange(item.startLine, item.endLine)
      );
    },
    [handleItemClick]
  );

  const handleLineClick = useCallback(
    (manifestId: string, lineNumber: number) => {
      const item = flatItems.find(
        (item) =>
          item.manifestId === manifestId &&
          isItemClickable(item) &&
          inRange(lineNumber, item.startLine, item.endLine + 1)
      );

      if (!item || !isItemClickable(item)) return;

      selectItem(item);
    },
    [flatItems, selectItem]
  );

  const selectFirstItem = useCallback(() => {
    const firstItem = flatItems.find(isItemClickable);

    if (firstItem) selectItem(firstItem);
  }, [flatItems, selectItem]);

  useEffect(() => {
    if (selectedItemId) return;

    selectFirstItem();
  }, [selectFirstItem, selectedItemId]);

  if (!workloadData) return null;

  return (
    <WorkloadDrawerView
      ref={ref}
      resourceName={workloadData.resourceName}
      categories={categories}
      yamlFiles={yamlFiles}
      selectedItemId={selectedItemId}
      activeManifestId={activeManifestId}
      highlightedLines={highlightedLines}
      isLoading={isLoadingDetails}
      onItemClick={handleItemClick}
      onLineClick={handleLineClick}
      onSuppressWorkload={handleSuppressWorkload}
      onSuppressRestrictionType={handleSuppressRestrictionType}
      {...drawerProps}
    />
  );
};
